import { GiphyFetch } from '@giphy/js-fetch-api'
import { IGif } from '@giphy/js-types'
import { VolumeOffIcon, VolumeOnIcon } from '@giphy/react-components'
import { useCallback, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useWindowSize } from 'react-use'
import Video_ from 'shared/components/video-player/sdk-video-with-ga'
import useAsyncEffect from 'shared/hooks/use-async-effect'
import styled from 'styled-components'
import Menu from './menu'

const Video = styled(Video_)`
    overflow: hidden;
`

type Props = {
    gif: IGif
    showAttribution?: boolean
}

const Container = styled.div`
    position: relative;
`

const Mute = styled.div`
    position: absolute;
    top: 10px;
    right: 10px;
    cursor: pointer;
`

const setTitle = (gif: IGif) => {
    if (title) gif.title = `${title} ${username ? `mixtape by ${username}` : ``}`
}

const qs = new URL(location.href).searchParams
const clips = (qs.get('clips') || '').split(',')
const title = qs.get('title')
const username = qs.get('username')
const gf = new GiphyFetch(window.GIPHY_FE_EMBED_KEY)

export const GACategory = 'Video Embed'
const PlaylistEmbed = ({ gif }: Props) => {
    // remove the ARP as requested to stop pingbacks
    // https://giphypedia.atlassian.net/browse/SDK-909
    gif.analytics_response_payload = ''
    setTitle(gif)
    const { width, height } = useWindowSize()
    const [userMuted, setUserMuted] = useState<boolean>()

    const [showCaptions, setShowCaptions] = useState(false)
    const [ref, inView] = useInView()
    const [playlistIndex, setPlaylistIndex] = useState(-1)
    const [playlistGif, setPlaylistGif] = useState<IGif>(gif)
    const videoEl = useRef<HTMLVideoElement | null>(null)
    // const reload = useRef(false)

    useAsyncEffect(async () => {
        if (playlistIndex !== -1) {
            const playlistId = clips?.[playlistIndex]
            if (playlistId && playlistId !== playlistGif?.id) {
                const newGif = await gf.gif(playlistId)
                setTitle(newGif.data)
                setPlaylistGif(newGif.data)
                const nextId = clips?.[playlistIndex + 1]
                if (nextId) {
                    gf.gif(nextId)
                }
            }
        } else {
            setPlaylistGif(gif)
        }
    }, [playlistIndex, playlistGif])
    // the overlay menu
    const Overlay = useCallback(
        ({ isHovered }) =>
            playlistGif ? (
                <>
                    <Menu
                        isHovered={isHovered}
                        gif={playlistGif}
                        toggleCaptions={() => {
                            setShowCaptions(!showCaptions)
                        }}
                    />
                    <Mute onClick={() => setUserMuted(!userMuted)}>
                        {userMuted ? <VolumeOffIcon /> : isHovered ? <VolumeOnIcon /> : null}
                    </Mute>
                </>
            ) : null,
        [showCaptions, playlistGif, userMuted]
    )
    return playlistGif ? (
        <Container ref={ref}>
            <Video
                gif={playlistGif}
                ccEnabled={showCaptions}
                muted={inView ? userMuted : true}
                width={width}
                height={height}
                hideMute
                hideAttribution
                onMuted={(browserMuted) => {
                    setUserMuted(browserMuted)
                }}
                onUserMuted={(muted) => {
                    setUserMuted(muted)
                }}
                setVideoEl={(el: HTMLVideoElement) => {
                    videoEl.current = el
                }}
                onCanPlay={() => {
                    // no loop, wait for on play, then play again
                    videoEl.current?.play()
                }}
                controls
                onEnded={() => {
                    if (playlistIndex === clips?.length - 1) {
                        setPlaylistIndex(-1)
                    } else {
                        setPlaylistIndex(playlistIndex + 1)
                    }
                }}
                loop={false}
                category={GACategory}
                overlay={Overlay}
            />
        </Container>
    ) : null
}

export default PlaylistEmbed
